import { getAdditionalOffers } from "../../../app/api/getOffers";
import { BaseAction } from "../../../core/actions/BaseAction";
import { isLiveChangeDateCheckout } from "../../../Lib/Utils/changeDate";
import { calculatePrice, isActionRunSuccessfully, isEmpty } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { AdmissionStatusEnum, TrainTypeEnum, TripTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";
import {
  IBookedOffer,
  IBookingResponse,
  ILeg,
  IOffer,
  ITrip,
  ITripProduct,
} from "../../../Orchestrator/Types/Index";
import { IGetAddonsParams } from "../../../Orchestrator/Types/Interface/ActionParams/GetAddonsParams";
import {
  IAncillaryOfferPart,
  ITransformedMeal,
  ITransformedMealForLegs,
} from "../../../Orchestrator/Types/Interface/IAddOns";
import { IAdditionalOffersResponse } from "../../../Orchestrator/Types/Types/OffersResponse";
import { getAllOfferWithoutExchangeAction } from "../CommonActions/GetAllOfferWithoutExchangeAction";
import { getAllTripWithoutExchangeAction } from "../CommonActions/GetAllTripWithoutExchangeAction";

export class GetAddonsFromBookingAction extends BaseAction<
  Partial<ITransformedMealForLegs>,
  IGetAddonsParams
> {
  protected override deduplicate = true;

  override async run(): Promise<Partial<ITransformedMealForLegs>> {
    const transformedMealForLegs: Partial<ITransformedMealForLegs> = {};
    try {
      const {
        bookingId = "",
        bookedOffers = [],
        bookedTrips = [],
        isUpgrade,
        isBookingDetails = false
      } = this.params!;
      //Get all the offer without exchange offer from trips
      let allBookedOfferWithoutExchange: IBookedOffer[] = []
      getAllOfferWithoutExchangeAction.setParams(bookedOffers)
      await getAllOfferWithoutExchangeAction.execute();
      if (isActionRunSuccessfully(getAllOfferWithoutExchangeAction)) {
        allBookedOfferWithoutExchange = getAllOfferWithoutExchangeAction?.result || []
        allBookedOfferWithoutExchange = allBookedOfferWithoutExchange.filter(d => d.admissions[0].status !== AdmissionStatusEnum.REFUNDED)
        // const isUpgrade1 = getRecoilStateValue("isUpgradeToPremiumStateAtom")
        if (isUpgrade) {
          allBookedOfferWithoutExchange = allBookedOfferWithoutExchange.filter(d => d.admissions[0].status === AdmissionStatusEnum.PREBOOKED)
        }
      }
      let allTripWithoutExchange: ITrip[] = []
      //Get all the trips without exchange trip from trips 
      getAllTripWithoutExchangeAction.setParams({ bookedOffers: allBookedOfferWithoutExchange, trips: bookedTrips })
      await getAllTripWithoutExchangeAction.execute();
      if (isActionRunSuccessfully(getAllTripWithoutExchangeAction)) {
        allTripWithoutExchange = getAllTripWithoutExchangeAction?.result || []
      }

      const isQuickExchange = getRecoilStateValue("isQuickExchangeStateAtom")
      let addonsForTripDirection = getRecoilStateValue("quickExchangeTripDirectionAtom") || ""
      if (addonsForTripDirection !== "")
        addonsForTripDirection = addonsForTripDirection === TrainTypeEnum.outbound ? TripTypeEnum.OUT_BOUND : TripTypeEnum.IN_BOUND

      // Manage-seating must never take the exchange-based PREBOOKED-only path:
      // in manage-seating every offer is FULFILLED, so that filter wipes all
      // offers and the meal fetch produces nothing → Extras card missing.
      // Ignore a stale isChangeDateFlow flag (left by an abandoned upgrade/CDT
      // attempt) when manage-seating is active. ManageSeatingRedirectClickAction
      // also clears the flag on entry; this is belt-and-suspenders.
      const isManageSeating = (getRecoilStateValue("isManageSeatingAtom") as boolean) || false
      // Booking-details forces the plain path: those flags can be stale from an
      // aborted change-date / quick-exchange checkout and would otherwise send
      // this read-only fetch down the prebooked-only exchange filter.
      const isChangeDateFlow = !isBookingDetails && isLiveChangeDateCheckout() && !isManageSeating
      const isQuickExchangeCheckout = !isBookingDetails && !!isQuickExchange && !isManageSeating
      const isExchangeBasedCheckout = isChangeDateFlow || isQuickExchangeCheckout
      let changeDateTripDirection = ""
      if (isExchangeBasedCheckout) {
        // Filter out offers that have EXCHANGE_ONGOING or EXCHANGED admissions on the
        // server side. GetAllOfferWithoutExchangeAction removes these admissions in
        // memory, but the offer ID still references the original offer. The
        // additional-offers API rejects requests for partially-exchanged offers.
        allBookedOfferWithoutExchange = allBookedOfferWithoutExchange.filter(offer => {
          const originalOffer = bookedOffers.find(o => o.offerId === offer.offerId);
          return !originalOffer?.admissions?.some(a => a.status === AdmissionStatusEnum.EXCHANGE_ONGOING || a.status === AdmissionStatusEnum.EXCHANGED);
        });

        // During an exchange-based checkout (CDT or prev/next quick-exchange),
        // additional-offers must be requested only for the newly created
        // prebooked offers. Older fulfilled offers in the same booking can
        // return BOOKING_OFFER_NOT_FOUND.
        const prebookedOnly = allBookedOfferWithoutExchange.filter(offer =>
          (offer.admissions || []).some(admission => admission.status === AdmissionStatusEnum.PREBOOKED)
        );
        allBookedOfferWithoutExchange = prebookedOnly;
      }

      if (isChangeDateFlow) {
        // CDT-specific direction filter — uses passenger-cancel selections.
        // Quick-exchange has its own direction via quickExchangeTripDirectionAtom
        // (already captured in addonsForTripDirection above).
        const selectedPassengerForCancel = (getRecoilStateValue("selectedPassengerForCancelAtom") as string[]) || []
        const selectedPassengerForCancelInBound = (getRecoilStateValue("selectedPassengerForCancelInBoundAtom") as string[]) || []

        if (selectedPassengerForCancelInBound.length > 0 && selectedPassengerForCancel.length === 0) {
          changeDateTripDirection = TripTypeEnum.IN_BOUND
        }
        else if (selectedPassengerForCancel.length > 0 && selectedPassengerForCancelInBound.length === 0) {
          changeDateTripDirection = TripTypeEnum.OUT_BOUND
        }
      }
      
      for (let i = 0; i < allTripWithoutExchange.length; i++) {
        const tripDirection = allTripWithoutExchange[i].direction
        // Use isQuickExchangeCheckout (respects isBookingDetails), not the raw
        // atom — otherwise a stale quickExchangeTripDirectionAtom would drop a
        // leg's addons on the booking-details fetch.
        if (addonsForTripDirection !== "" && isQuickExchangeCheckout) {
          if (addonsForTripDirection !== tripDirection) continue
        }
        
        if (isChangeDateFlow && changeDateTripDirection !== "") {
          if (changeDateTripDirection !== tripDirection) continue
        }
        
        const allTripLegs = (allTripWithoutExchange[i]?.legs || [])?.filter((leg: any) => leg?.timedLeg?.service?.mode?.ptMode === "TRAIN");
        for (const leg of allTripLegs) {
          const offerDetails = allBookedOfferWithoutExchange?.find((offer) => {
            if (offer.tripCoverage.coveredTripId !== allTripWithoutExchange[i].id) return false;
            return (offer.reservations || [])?.some(res =>
              (res.tripCoverage?.coveredLegIds || [])?.includes(leg.id)
            );
          });
          if (!offerDetails) continue;
          
          const additionOfferResponse: any = await getAdditionalOffers(
            bookingId,
            offerDetails.offerId
          );
          if (additionOfferResponse.answer as IAdditionalOffersResponse) {
            const offers: IOffer[] = additionOfferResponse?.answer?.offers || [];
            const parseOrderTag = (tags: string[] = []): { categoryOrder: number; subOrder: number } => {
              const orderTag = (tags || []).find((t) => /^ORDER_\d+\.\d+$/.test(t));
              if (!orderTag) return { categoryOrder: Number.MAX_SAFE_INTEGER, subOrder: Number.MAX_SAFE_INTEGER };
              const [, order] = orderTag.split("_");
              const [catStr, subStr] = order.split(".");
              const categoryOrder = Number(catStr);
              const subOrder = Number(subStr);
              return {
                categoryOrder: Number.isFinite(categoryOrder) ? categoryOrder : Number.MAX_SAFE_INTEGER,
                subOrder: Number.isFinite(subOrder) ? subOrder : Number.MAX_SAFE_INTEGER,
              };
            };

            const uniqueCategories = Array.from(
              new Set(
                offers.flatMap((d) => d.ancillaryOfferParts.map((a: any) => a.category))
              )
            );

            const getAllCategory = uniqueCategories
              .map((category) => ({ category }))
              .sort((a, b) => 0)
              .map((d) => d.category);
            if (getAllCategory.length > 0) {
              //Get Trip legs
              const allTripLegs = allTripWithoutExchange[i]?.legs || [];
              //Map a loop and get all Addons based on leg id from trip
              // allTripLegs.forEach((leg: ILeg) => {
                const categoryWithOrder = uniqueCategories.map((category) => {
                  const allAncillaryOfferPartsForCategory: IAncillaryOfferPart[] = offers
                    .filter((o: IOffer) =>
                      o?.ancillaryOfferParts?.some(
                        (part: IAncillaryOfferPart) =>
                          part?.category === category &&
                          part.tripCoverage.coveredLegIds[0] === leg.id
                      )
                    )
                    .flatMap((o: IOffer) => o.ancillaryOfferParts)
                    .filter((d) => d?.category === category);

                  const allAncillaryProductIdsForCategory: string[] =
                    allAncillaryOfferPartsForCategory
                      .map((d) => d.products)
                      .flatMap((p) => p)
                      .map((d) => d.productId);

                  const allOfferProductPartsForCategory: ITripProduct[] = offers
                    .flatMap((o: IOffer) => o.products || [])
                    .filter((product: ITripProduct) =>
                      allAncillaryProductIdsForCategory.includes(product.id)
                    );

                  const minCategoryOrder = allOfferProductPartsForCategory.reduce((min, prod) => {
                    const { categoryOrder } = parseOrderTag(prod.productTags || []);
                    return Math.min(min, categoryOrder);
                  }, Number.MAX_SAFE_INTEGER);

                  return { category, order: minCategoryOrder };
                });

                const sortedCategoriesForLeg = categoryWithOrder
                  .sort((a, b) => a.order - b.order)
                  .map((d) => d.category);

                sortedCategoriesForLeg.forEach((category) => {
                  // Get ancillary offer of category and lag
                  const allAncillaryOfferParts: IAncillaryOfferPart[] = offers
                    .filter((o: IOffer) =>
                      o?.ancillaryOfferParts?.some(
                        (part: IAncillaryOfferPart) =>
                          part?.category === category &&
                          part.tripCoverage.coveredLegIds[0] === leg.id
                      )
                    )
                    .flatMap((o: IOffer) => o.ancillaryOfferParts)
                    .filter((d) => d?.category === category);

                  // Get ancillary offer of category and lag
                  const allAncillaryProductIds: string[] =
                    allAncillaryOfferParts
                      .map((d) => d.products)
                      .flatMap((p) => p)
                      .map((d) => d.productId);

                  const allOfferProductParts: ITripProduct[] = offers
                    .flatMap((o: IOffer) => o.products || [])
                    .filter((product: ITripProduct) =>
                      allAncillaryProductIds.includes(product.id)
                    );

                  const offersWithOrder = allAncillaryOfferParts
                    .map((legAncillaryOffer) => {
                      const legProduct = allOfferProductParts.find((product) =>
                        legAncillaryOffer?.products
                          .map((p) => p.productId)
                          .includes(product.id)
                      );
                      const order = parseOrderTag(legProduct?.productTags || []);
                      return { legAncillaryOffer, legProduct, order };
                    })
                    .sort((a, b) => a.order.subOrder - b.order.subOrder);

                  offersWithOrder.forEach(({ legAncillaryOffer, legProduct }) => {
                    if (legProduct) {
                      const additionalOfferId = offers.find(d => d.ancillaryOfferParts.some(d => d.id === legAncillaryOffer.id))
                      const productTag = (legProduct?.productTags || [])
                      let icon = ""                      
                      if (productTag.includes("VEG_MEAL")) {
                        icon = "/assets/booking/leaf.svg"
                      } else if (productTag.includes("NO_VEG_MEAL")) {
                        icon = "/assets/booking/non-veg.svg"
                      }
                      // Prepare a Addons with leg                      
                      const addonsDetails: ITransformedMeal = {
                        price: calculatePrice({
                          amount: legAncillaryOffer.price.amount,
                          scale: legAncillaryOffer.price.scale,
                          currency: "",
                        }),
                        icon: icon,
                        // Backend-localized category heading. `ancillaryOfferParts[].type`
                        // is the human-readable, locale-aware category name (e.g.
                        // "مسار الحافلة - الكورنيش"); the `category` field stays English
                        // and is used only as the grouping/logic key.
                        categoryLabel: legAncillaryOffer.type,
                        label: legProduct.description,
                        // Longer human text for the (i) tooltip (e.g. shuttle details).
                        // `description` is the short label, so prefer descriptiveTexts.
                        // First element is the primary text; textElementType is not
                        // filtered (verified correct on live shuttle data).
                        description:
                          legProduct.descriptiveTexts?.[0]?.description ||
                          legProduct.descriptiveTexts?.[0]?.shortDescription ||
                          "",
                        type: legProduct.code,
                        ancillaryOfferId: legAncillaryOffer?.id || "", // product id
                        bookedOfferId: offerDetails.offerId,
                        additionalOfferId: additionalOfferId?.offerId || "", // Additional Offers, offerId,
                        tripCoverageId:
                          legAncillaryOffer.tripCoverage.coveredTripId,
                        coveredLegId:
                          (legAncillaryOffer?.tripCoverage?.coveredLegIds || [])
                            .length > 0
                            ? legAncillaryOffer?.tripCoverage?.coveredLegIds[0]
                            : "",
                        passengerRef: "",
                      };                      

                      if (
                        !transformedMealForLegs[leg.id] ||
                        isEmpty(transformedMealForLegs[leg.id])
                      ) {
                        transformedMealForLegs[leg.id] = {
                          [category]: [addonsDetails],
                        };
                      } else {
                        const existingCategory =
                          transformedMealForLegs[leg.id]?.[category] || [];
                        existingCategory.push(addonsDetails);

                        transformedMealForLegs[leg.id] = {
                          ...transformedMealForLegs[leg.id],
                          [category]: existingCategory,
                        };
                      }
                    }
                  });
                // });
              });
            }
          }
        }
      }
      // Booking-details keeps the modal-mount tree alive across fast A->B
      // navigation, so a slower fetch for booking A can resolve after B is
      // already active and stomp B's meals with A's data. Checkout never
      // switches bookings mid-flight, so its write stays unconditional.
      const activeBookingId = isBookingDetails
        ? (getRecoilStateValue("bookingResponseAtom") as IBookingResponse | null)?.booking?.id
        : bookingId;
      if (activeBookingId === bookingId) {
        updateRecoilState(
          "mealPreferencesResponseAtom",
          transformedMealForLegs
        );
      }
    } catch (error) {
      console.error("Error in GetAddonsFromBookingAction:", error);
    } finally {
      return transformedMealForLegs;
    }
  }
}

export const getAddonsFromBookingAction =
  new GetAddonsFromBookingAction();
